import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from "react-router-dom";
import "../../../feature-styles/auth.css";
import { FaLock, FaShieldAlt, FaSignInAlt, FaEye, FaEyeSlash, FaExclamationTriangle, FaClock } from "react-icons/fa";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { authApi } from "../../auth/api/auth.api";
import { resetPasswordSchema } from "../../auth/schemas/reset-password.schema";
import { toast } from "react-hot-toast";

/** Estados del enlace */
const STATUS = { CHECKING: 'checking', VALID: 'VALID', EXPIRED: 'EXPIRED', INVALID: 'INVALID', USED: 'USED' };

export default function ResetPassword() {
    const { register, handleSubmit, formState: { errors } } = useForm({
        resolver: zodResolver(resetPasswordSchema)
    });
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");

    const [tokenStatus, setTokenStatus] = useState(STATUS.CHECKING);
    const [obscure, setObscure] = useState(true);
    const [obscureConfirm, setObscureConfirm] = useState(true);
    const [loading, setLoading] = useState(false);

    // Validar el token al montar el componente
    useEffect(() => {
        if (!token) {
            setTokenStatus(STATUS.INVALID);
            return;
        }
        authApi.validateResetToken(token)
            .then(res => setTokenStatus(res.status || STATUS.INVALID))
            .catch(() => setTokenStatus(STATUS.INVALID));
    }, [token]);

    const onSubmit = async (data) => {
        setLoading(true);
        try {
            await authApi.resetPassword({ token, newPassword: data.password });
            toast.success("¡Contraseña restablecida! Ahora puedes iniciar sesión.");
            navigate('/');
        } catch (error) {
            const msg = error.message || "No se pudo restablecer la contraseña.";
            // Si el backend dice que expiró o fue usado, actualizar estado visual
            if (msg.toLowerCase().includes("utilizado")) {
                setTokenStatus(STATUS.USED);
            } else if (msg.toLowerCase().includes("expir")) {
                setTokenStatus(STATUS.EXPIRED);
            } else {
                toast.error(msg);
            }
        } finally {
            setLoading(false);
        }
    };

    // ── Pantalla de carga ──────────────────────────────────────────────────────
    if (tokenStatus === STATUS.CHECKING) {
        return (
            <div className="main-div">
                <div className="secondary-div" style={{ textAlign: 'center', padding: '40px' }}>
                    <div className="panel-header">
                        <FaShieldAlt className="panel-header-icon" />
                        <h1 className="panel-header-title">VERIFICANDO ENLACE...</h1>
                    </div>
                    <p style={{ marginTop: 20, opacity: 0.7 }}>Por favor espera un momento.</p>
                </div>
            </div>
        );
    }

    // ── Enlace expirado ────────────────────────────────────────────────────────
    if (tokenStatus === STATUS.EXPIRED) {
        return (
            <div className="main-div">
                <div className="secondary-div">
                    <div className="panel-header" style={{ background: 'linear-gradient(135deg, #c0392b, #e74c3c)' }}>
                        <FaClock className="panel-header-icon" />
                        <h1 className="panel-header-title">ENLACE EXPIRADO</h1>
                    </div>
                    <div className="panel-body" style={{ textAlign: 'center', padding: '32px 24px' }}>
                        <p style={{ marginBottom: 16, lineHeight: 1.6 }}>
                            El enlace de recuperación <strong>ya no es válido</strong>.<br />
                            Los enlaces expiran exactamente a los <strong>10 minutos</strong> por seguridad.
                        </p>
                        <button className="btn-access" onClick={() => navigate('/')}>
                            Volver al inicio de sesión
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ── Enlace utilizado ───────────────────────────────────────────────────────
    if (tokenStatus === STATUS.USED) {
        return (
            <div className="main-div">
                <div className="secondary-div">
                    <div className="panel-header" style={{ background: 'linear-gradient(135deg, #e67e22, #d35400)' }}>
                        <FaExclamationTriangle className="panel-header-icon" />
                        <h1 className="panel-header-title">ENLACE UTILIZADO</h1>
                    </div>
                    <div className="panel-body" style={{ textAlign: 'center', padding: '32px 24px' }}>
                        <p style={{ marginBottom: 16, lineHeight: 1.6 }}>
                            Este enlace de recuperación <strong>ya fue utilizado</strong>.<br />
                            Solo se puede usar una vez por seguridad. Si necesitas restablecer tu contraseña nuevamente, solicita un nuevo enlace.
                        </p>
                        <button className="btn-access" onClick={() => navigate('/')}>
                            Volver al inicio de sesión
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ── Enlace inválido ────────────────────────────────────────────────────────
    if (tokenStatus === STATUS.INVALID) {
        return (
            <div className="main-div">
                <div className="secondary-div">
                    <div className="panel-header" style={{ background: 'linear-gradient(135deg, #7f8c8d, #95a5a6)' }}>
                        <FaExclamationTriangle className="panel-header-icon" />
                        <h1 className="panel-header-title">ENLACE NO VÁLIDO</h1>
                    </div>
                    <div className="panel-body" style={{ textAlign: 'center', padding: '32px 24px' }}>
                        <p style={{ marginBottom: 16, lineHeight: 1.6 }}>
                            Este enlace de recuperación <strong>no es válido</strong> o ya fue utilizado.<br />
                            Si necesitas restablecer tu contraseña, solicita un nuevo enlace.
                        </p>
                        <button className="btn-access" onClick={() => navigate('/')}>
                            Volver al inicio de sesión
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ── Formulario (token VÁLIDO) ──────────────────────────────────────────────
    return (
        <div className="main-div">
            <div className="secondary-div">
                <div className="panel-header">
                    <FaShieldAlt className="panel-header-icon" />
                    <h1 className="panel-header-title">RESTABLECER CONTRASEÑA</h1>
                </div>
                <div className="panel-body">
                    <h2 className="h2-title">Ingresa tu nueva contraseña</h2>
                    <p style={{ fontSize: 13, opacity: 0.65, marginBottom: 16, textAlign: 'center' }}>
                        Este enlace es válido por <strong>10 minutos</strong>.
                    </p>
                    <form className="form-modal" onSubmit={handleSubmit(onSubmit)}>

                        <label className="label-input">Nueva Contraseña</label>
                        <div className="password-inline-login">
                            <FaLock className="icon-input" />
                            <input
                                className="input-plain-login"
                                type={obscure ? "password" : "text"}
                                placeholder="Mín. 8 cars., mayúscula, número y símbolo"
                                {...register("password")}
                            />
                            <span onClick={() => setObscure(!obscure)} className="eye-toggle">
                                {obscure ? <FaEyeSlash /> : <FaEye />}
                            </span>
                        </div>
                        <div className="text-start">
                            {errors.password && <span className="span-error-form">{errors.password.message}</span>}
                        </div>

                        <label className="label-input">Confirmar Contraseña</label>
                        <div className="password-inline-login">
                            <FaLock className="icon-input" />
                            <input
                                className="input-plain-login"
                                type={obscureConfirm ? "password" : "text"}
                                placeholder="Confirma nueva contraseña"
                                {...register("confirmPassword")}
                            />
                            <span onClick={() => setObscureConfirm(!obscureConfirm)} className="eye-toggle">
                                {obscureConfirm ? <FaEyeSlash /> : <FaEye />}
                            </span>
                        </div>
                        <div className="text-start">
                            {errors.confirmPassword && <span className="span-error-form">{errors.confirmPassword.message}</span>}
                        </div>

                        <button type="submit" disabled={loading} className="btn-access" style={{ marginTop: '20px' }}>
                            <FaSignInAlt />
                            {loading ? "Procesando..." : "RESTABLECER"}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
